import { Card, CardContent } from '@/components/ui-kit/card';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useSaveAuthConfig, useGetToolDetails } from '@/modules/gpt-chats/hooks/use-tools';
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast';

import { ToolAuthCard } from '@/modules/gpt-chats/components/tools/tool-card/tool-auth-card';
import { useForm, FormProvider } from 'react-hook-form';
import type {
  AuthType,
  AuthLocation,
  ISaveAuthConfigPayload,
  IAuthConfigPayload,
} from '@/modules/gpt-chats/types/tools.service.type';
import { useSearchParams } from '@/lib/react-router-compat';
import { useTranslation } from 'react-i18next';

interface HeaderField {
  id: string;
  key: string;
  value: string;
}

interface AuthFormValues {
  authType: AuthType;
  authLocation: AuthLocation;
  authKeyName: string;
  authKeyPrefix: string;
  apiToken: string;
  username: string;
  password: string;
  clientId: string;
  clientSecret: string;
  tokenUrl: string;
  scopes: string;
  grantType: 'authorization_code' | 'client_credentials' | 'refresh_token';
  customHeaders: HeaderField[];
  oauth2Headers: HeaderField[];
}

export const ToolAuthentication = ({ toolId }: { toolId: string }) => {
  const form = useForm<AuthFormValues>({
    defaultValues: {
      authType: 'none',
      authLocation: 'header',
      authKeyName: '',
      authKeyPrefix: '',
      apiToken: '',
      username: '',
      password: '',
      clientId: '',
      clientSecret: '',
      tokenUrl: '',
      scopes: '',
      grantType: 'client_credentials',
      customHeaders: [],
      oauth2Headers: [{ id: '1', key: '', value: '' }],
    },
  });
  const { t } = useTranslation();

  const [customHeaders, setCustomHeaders] = useState<HeaderField[]>([
    { id: '1', key: '', value: '' },
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const searchParams = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';

  const { data: toolDetails } = useGetToolDetails(toolId, isBlocksTool);
  const { mutate: saveAuthConfig, isPending } = useSaveAuthConfig();

  useEffect(() => {
    if (toolDetails?.APIConfig?.AuthConfig) {
      const authConfig = toolDetails.APIConfig.AuthConfig;

      const formValues: Partial<AuthFormValues> = {};

      if (authConfig.AuthType) {
        formValues.authType = authConfig.AuthType;
      }
      if (authConfig.AuthKeyLocation) {
        formValues.authLocation = authConfig.AuthKeyLocation;
      }
      if (authConfig.AuthKeyName) {
        formValues.authKeyName = authConfig.AuthKeyName;
      }
      if (authConfig.AuthKeyPrefix) {
        formValues.authKeyPrefix = authConfig.AuthKeyPrefix;
      }
      if (authConfig.APIToken) {
        formValues.apiToken = authConfig.APIToken;
      }
      if (authConfig.Username) {
        formValues.username = authConfig.Username;
      }
      if (authConfig.Password) {
        formValues.password = authConfig.Password;
      }

      if (authConfig.OAuth2Config) {
        if (authConfig.OAuth2Config.ClientID) {
          formValues.clientId = authConfig.OAuth2Config.ClientID;
        }
        if (authConfig.OAuth2Config.ClientSecret) {
          formValues.clientSecret = authConfig.OAuth2Config.ClientSecret;
        }
        if (authConfig.OAuth2Config.TokenURL) {
          formValues.tokenUrl = authConfig.OAuth2Config.TokenURL;
        }
        if (authConfig.OAuth2Config.Scopes) {
          formValues.scopes = authConfig.OAuth2Config.Scopes.join(', ');
        }
        if (authConfig.OAuth2Config.GrantType) {
          formValues.grantType = authConfig.OAuth2Config.GrantType;
        }
        if (authConfig.CustomHeaders && Object.keys(authConfig.CustomHeaders).length > 0) {
          formValues.oauth2Headers = Object.entries(authConfig.CustomHeaders).map(
            ([key, value], index) => ({
              id: (index + 1).toString(),
              key,
              value,
            })
          );
        }
      }

      form.reset({ ...form.getValues(), ...formValues });
    }

    if (toolDetails?.APIConfig?.Headers && Object.keys(toolDetails.APIConfig.Headers).length > 0) {
      const headers = Object.entries(toolDetails.APIConfig.Headers).map(([key, value], index) => ({
        id: (index + 1).toString(),
        key,
        value,
      }));
      setCustomHeaders(headers);
    }
  }, [toolDetails, form]);

  const addHeaderField = () => {
    setCustomHeaders([...customHeaders, { id: Date.now().toString(), key: '', value: '' }]);
  };

  const removeHeaderField = (id: string) => {
    setCustomHeaders(customHeaders.filter((field) => field.id !== id));
  };

  const updateHeaderField = (id: string, field: 'key' | 'value', newValue: string) => {
    setCustomHeaders(
      customHeaders.map((item) => (item.id === id ? { ...item, [field]: newValue } : item))
    );
  };

  const validate = () => {
    const values = form.getValues();
    const newErrors: Record<string, string> = {};

    if (values.authType === 'bearer') {
      if (!values.authKeyName) newErrors.authKeyName = t('KEY_IS_REQUIRED');
      if (!values.apiToken) newErrors.apiToken = t('SECRET_IS_REQUIRED');
      if (!values.authKeyPrefix) newErrors.authKeyPrefix = t('PREFIX_IS_REQUIRED');
    } else if (values.authType === 'basic') {
      if (!values.username) newErrors.username = t('USERNAME_IS_REQUIRED');
      if (!values.password) newErrors.password = t('PASSWORD_IS_REQUIRED');
      if (!values.authKeyPrefix) newErrors.authKeyPrefix = t('PREFIX_IS_REQUIRED');
    } else if (values.authType === 'api_token') {
      if (!values.authKeyName) newErrors.authKeyName = t('KEY_IS_REQUIRED');
      if (!values.apiToken) newErrors.apiToken = t('SECRET_IS_REQUIRED');
      if (!values.authKeyPrefix) newErrors.authKeyPrefix = t('PREFIX_IS_REQUIRED');
    } else if (values.authType === 'oauth2') {
      if (!values.clientId) newErrors.clientId = t('CLIENT_ID_IS_REQUIRED');
      if (!values.clientSecret) newErrors.clientSecret = t('CLIENT_SECRET_IS_REQUIRED');
      if (!values.tokenUrl) newErrors.tokenUrl = t('TOKEN_URL_IS_REQUIRED');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      showErrorToast({ errors: t('FILL_REQUIRED_FIELDS') });
      return;
    }

    const values = form.getValues();
    const headersObject: Record<string, string> = {};

    customHeaders.forEach((header) => {
      if (header.key && header.value) {
        headersObject[header.key] = header.value;
      }
    });

    const authConfig: Partial<IAuthConfigPayload> = {
      AuthType: values.authType,
    };

    if (values.authType !== 'none') {
      if (values.authLocation) authConfig.AuthKeyLocation = values.authLocation;
      if (values.authKeyName) authConfig.AuthKeyName = values.authKeyName;
      if (values.authKeyPrefix) authConfig.AuthKeyPrefix = values.authKeyPrefix;
      if (values.apiToken) authConfig.APIToken = values.apiToken;
      if (values.username) authConfig.Username = values.username;
      if (values.password) authConfig.Password = values.password;

      if (values.authType === 'oauth2') {
        authConfig.OAuth2Config = {
          ClientID: values.clientId,
          ClientSecret: values.clientSecret,
          TokenURL: values.tokenUrl,
          Scopes: values.scopes ? values.scopes.split(',').map((s) => s.trim()) : [],
          GrantType: values.grantType,
        };

        authConfig.CustomHeaders = values.oauth2Headers?.reduce(
          (acc, curr) => {
            if (curr.key && curr.value) {
              acc[curr.key] = curr.value;
            }
            return acc;
          },
          {} as Record<string, string>
        );
      }
    }

    const payload: ISaveAuthConfigPayload = {
      auth_config: authConfig as IAuthConfigPayload,
      ...(Object.keys(headersObject).length > 0 && { headers: headersObject }),
      is_open_tool: isBlocksTool,
    };

    saveAuthConfig(
      { toolId, payload },
      {
        onSuccess: (res) => {
          if (res.is_success !== false) {
            showSuccessToast({ description: t('AUTH_CONFIG_SAVED_SUCCESSFULLY') });
          } else {
            showErrorToast({
              errors: [
                {
                  message: res.message || res.detail || t('FAILED_TO_SAVE_AUTH_CONFIG'),
                },
              ],
            });
          }
        },
        onError: (error: unknown) => {
          let errorMessage = t('ERROR_SAVING_AUTH_CONFIG');

          if (error && typeof error === 'object' && 'response' in error) {
            const response = (
              error as { response?: { data?: { detail?: string | Array<{ msg: string }> } } }
            ).response;

            if (response?.data?.detail) {
              if (typeof response.data.detail === 'string') {
                errorMessage = response.data.detail;
              } else if (Array.isArray(response.data.detail)) {
                errorMessage = response.data.detail.map((err) => err.msg).join(', ');
              }
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }

          console.error('Save auth config error:', error);
          showErrorToast({ errors: [{ message: errorMessage }] });
        },
      }
    );
  };

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-6">
        <ToolAuthCard errors={errors} />

        {
          <Card>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{t('HEADERS')}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{t('CUSTOM_HEADERS_DESCRIPTION')}</p>

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 font-medium">
                    <Label>{t('KEY')}</Label>
                    <Label>{t('VALUE')}</Label>
                  </div>

                  {customHeaders.map((field) => (
                    <div key={field.id} className="flex items-center gap-4">
                      <Input
                        placeholder={t('HEADER_KEY')}
                        value={field.key}
                        onChange={(e) => updateHeaderField(field.id, 'key', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        placeholder={t('ENTER_VALUE')}
                        value={field.value}
                        onChange={(e) => updateHeaderField(field.id, 'value', e.target.value)}
                        className="flex-1"
                      />
                      {customHeaders.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeHeaderField(field.id)}
                          className="h-10 w-10 flex-shrink-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addHeaderField}
                    className="w-fit gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t('ADD')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        }

        <div className="flex justify-start">
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? t('SAVING') : t('SAVE')}
          </Button>
        </div>
      </div>
    </FormProvider>
  );
};
